import { HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { DestroyRef, inject } from '@angular/core';
import { BehaviorSubject, catchError, throwError, switchMap, filter, take } from 'rxjs';
import { AuthService } from './auth.service';
import { TokenStorageService } from './token-storage.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const TOKEN_HEADER_KEY = 'Authorization';

export function authInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn) {
  let isRefreshing = false;
  let authReq = request;
  const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  const authService = inject(AuthService);
  const tokenService = inject(TokenStorageService);
  const destroyRef = inject(DestroyRef);
  const token = tokenService.getToken();

  if (token != null) {
    authReq = addTokenHeader(request, token);
  }

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if the error is due to an expired access token
      if (
        error instanceof HttpErrorResponse &&
        !authReq.url.includes('users/sign-in') &&
        !authReq.url.includes('users/refresh-tokens') &&
        error.status === 401
      ) {
        return handle401Error(request, next);
      }
      if (error instanceof HttpErrorResponse && authReq.url.includes('users/refresh-tokens')) {
        authService.logout().pipe(takeUntilDestroyed(destroyRef)).subscribe();
      }

      return throwError(() => error);
    }),
  );

  function handle401Error(request: HttpRequest<unknown>, next: HttpHandlerFn) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      const token = tokenService.getRefreshToken();

      if (token) {
        return authService.refreshAccessToken(token).pipe(
          switchMap((resData) => {
            isRefreshing = false;

            tokenService.saveToken(resData.accessToken);
            tokenService.saveRefreshToken(resData.refreshToken);
            refreshTokenSubject.next(resData.accessToken);

            return next(addTokenHeader(request, resData.accessToken));
          }),
          catchError((err) => {
            isRefreshing = false;

            tokenService.logout();

            return throwError(() => err);
          }),
        );
      }
    }

    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addTokenHeader(request, token))),
    );
  }

  function addTokenHeader(request: HttpRequest<unknown>, token: string) {
    return request.clone({
      setHeaders: {
        [TOKEN_HEADER_KEY]: `Bearer ${token}`,
      },
    });
  }
}
